import { Injectable, OnDestroy } from '@angular/core';
import { ChartUtils } from '../utils/chart-utils';
import { DateUtils } from '../utils/date-utils';

@Injectable()
export class ChartBuilderService implements OnDestroy {
  private readonly chartCache = new Map<string, any>();
  private readonly MAX_CACHE_SIZE = 50;

  ngOnDestroy(): void {
    this.clearCache();
  }

  private getCacheKey(method: string, data: any): string {
    try {
      return `${method}::${JSON.stringify(data)}`;
    } catch {
      return `${method}::__unstable__${Math.random()}`;
    }
  }

  private getFromCache(key: string): any | undefined {
    if (!this.chartCache.has(key)) return undefined;
    const value = this.chartCache.get(key);
    this.chartCache.delete(key);
    this.chartCache.set(key, value);
    return value;
  }

  private setInCache(key: string, value: any): void {
    if (key.includes('__unstable__')) return;
    if (this.chartCache.size >= this.MAX_CACHE_SIZE) {
      const firstKey = this.chartCache.keys().next().value;
      if (firstKey !== undefined) {
        this.chartCache.delete(firstKey);
      }
    }
    this.chartCache.set(key, value);
  }

  clearCache(): void {
    this.chartCache.clear();
  }

  // ================================================================
  // PUBLIC BUILD METHODS
  // ================================================================

  buildHorizontalBarChart(
    response: any[],
    nameKey: string,
    upcomingKey: string = 'upcoming',
    completedKey: string = 'completed',
  ): any {
    const cacheKey = this.getCacheKey('horizontalBarChart', {
      response,
      nameKey,
      upcomingKey,
      completedKey,
    });
    const cached = this.getFromCache(cacheKey);
    if (cached) return cached;

    let src = response?.length
      ? response
      : [{ [nameKey]: 'No Data', [upcomingKey]: 0, [completedKey]: 0 }];

    const categories: string[] = [];
    const upcomingData: number[] = [];
    const completedData: number[] = [];

    src.forEach((item) => {
      categories.push(item[nameKey] || 'Unknown');
      upcomingData.push(item[upcomingKey] ?? 0);
      completedData.push(item[completedKey] ?? 0);
    });

    const BAR_HEIGHT = 24;
    const BAR_GAP    = 14;
    const CHART_PAD  = 70;
    const chartHeight = Math.max(200, categories.length * (BAR_HEIGHT + BAR_GAP) + CHART_PAD);

    const maxTotal = Math.max(
      ...src.map(
        (item) => (item[upcomingKey] ?? 0) + (item[completedKey] ?? 0),
      ),
      0,
    );

    const getNiceTicks = (max: number) => {
      const targetTickCount = 6;
      let interval = Math.ceil(max / targetTickCount);
      const niceIntervals = [10, 20, 25, 50, 100, 200, 250, 500, 1000];
      for (const nice of niceIntervals) {
        if (interval <= nice) {
          interval = nice;
          break;
        }
      }
      const maxTick = Math.ceil(max / interval) * interval;
      return { interval, maxTick };
    };

    const { interval, maxTick } = getNiceTicks(maxTotal);
    const xAxisMax = maxTick;

    const config = {
      series: [
        { name: 'Upcoming', data: upcomingData },
        { name: 'Completed', data: completedData },
      ],
      chart: {
        type: 'bar',
        height: chartHeight,
        stacked: true,
        toolbar: { show: false },
        animations: {
          enabled: true,
          speed: 800,
          animateGradually: { enabled: true, delay: 150 },
        },
        background: '#404040',
      },
      colors: ['#2CC3E8', '#50B748'],
      plotOptions: {
        bar: {
          borderRadius: 0,
          borderRadiusApplication: 'end',
          horizontal: true,
          barHeight: `${BAR_HEIGHT}px`,
          dataLabels: {
            position: 'top',
            total: {
              enabled: true,
              style: {
                fontSize: '10px',
                fontWeight: 'bold',
                color: '#ffffff',
              },
              formatter: (val: number) => (val > 0 ? val.toString() : ''),
            },
          },
        },
      },

      dataLabels: {
        enabled: false,
      },

      xaxis: {
        categories,
        min: 0,
        max: xAxisMax,
        tickAmount: Math.ceil(xAxisMax / interval),
        labels: {
          style: { colors: '#94a3b8', fontSize: '11px', fontWeight: 400 },
          formatter: (value: number) => Math.round(value).toString(),
        },
        axisBorder: { show: true, color: '#404040' },
        axisTicks: { show: true, color: '#404040' },
      },
      yaxis: {
        labels: {
          style: { colors: '#e2e8f0', fontSize: '12px', fontWeight: 500 },
          offsetX: -5,
        },
      },
      grid: {
        borderColor: '#e2e8f0',
        strokeDashArray: 2,
        yaxis: { lines: { show: false } },
        xaxis: { lines: { show: true } },
      },
      tooltip: {
        enabled: true,
        theme: 'dark',
        style: { fontSize: '12px', fontFamily: 'Arial, sans-serif' },
        y: { formatter: (val: number) => val.toString() },
      },
      legend: { show: false },
      states: { hover: { filter: { type: 'darken', value: 0.15 } } },
      noData: {
        text: 'No data available',
        align: 'center',
        verticalAlign: 'middle',
        style: { color: '#fff', fontSize: '10px' },
      },
    };

    this.setInCache(cacheKey, config);
    return config;
  }

  buildMonthEventChart(data: any[], startDate: Date, endDate: Date): any {
    const cacheKey = this.getCacheKey('monthEventChart', {
      data,
      startDate,
      endDate,
    });
    const cached = this.getFromCache(cacheKey);
    if (cached) return cached;

    const sortedData = this.getMonthsInRange(startDate, endDate, data);
    const categories = sortedData.map((item) => item.monthYear || '');

    const upcomingSeries = sortedData.map((item) =>
      (item?.upcoming ?? 0) > 0 ? item.upcoming : null,
    );
    const completedSeries = sortedData.map((item) =>
      (item?.completed ?? 0) > 0 ? item.completed : null,
    );

    const rawMax = Math.max(
      ...sortedData.map(
        (item) => (item?.upcoming ?? 0) + (item?.completed ?? 0),
      ),
      1,
    );
    const yAxisMax = this.calculateYAxisMax(rawMax);
    const chartHeight = Math.min(600, Math.max(350, categories.length * 35));

    const config = {
      series: [
        {
          name: 'Upcoming events',
          data: upcomingSeries,
          type: 'bar',
          group: 'group1',
        },
        {
          name: 'Completed events',
          data: completedSeries,
          type: 'bar',
          group: 'group2',
        },
      ],
      chart: {
        type: 'bar',
        height: chartHeight,
        width: '100%',
        background: '#404040',
        toolbar: { show: false },
        zoom: { enabled: false },
        stacked: false,
      },
      colors: ['#2CC3E8', '#50B748'],
      plotOptions: {
        bar: {
          horizontal: false,
          columnWidth: '30%',
          borderRadius: 0,
          borderRadiusApplication: 'end',
          dataLabels: { position: 'top' },
          grouping: true,
        },
      },
      dataLabels: {
        enabled: true,
        offsetY: -15,
        style: {
          fontSize: '10px',
          fontWeight: 'bold',
          colors: ['#ffffff', '#ffffff'],
        },
        background: {
          enabled: false,
        },
        formatter: (val: number | null) =>
          val !== null && val > 0 ? val.toString() : '',
      },
      xaxis: {
        categories,
        labels: {
          style: { colors: '#94a3b8', fontSize: '10px', fontWeight: 500 },
          rotate: -45,
          rotateAlways: categories.length > 8,
        },
        axisBorder: { show: true, color: '#404040' },
        axisTicks: { show: true, color: '#404040' },
      },
      yaxis: {
        min: 0,
        max: yAxisMax,
        tickAmount: Math.ceil(yAxisMax / this.getTickInterval(yAxisMax)),
        labels: {
          style: { colors: '#94a3b8', fontSize: '11px', fontWeight: 500 },
          formatter: (value: number) => Math.round(value).toString(),
        },
        axisBorder: { show: true, color: '#404040' },
        axisTicks: { show: true, color: '#404040' },
      },
      grid: {
        borderColor: '#404040',
        strokeDashArray: 2,
        yaxis: { lines: { show: true } },
        xaxis: { lines: { show: false } },
      },
      stroke: {
        show: true,
        width: 5,
        colors: ['transparent'],
      },
      tooltip: {
        enabled: true,
        theme: 'dark',
        shared: true,
        intersect: false,
        y: {
          formatter: (val: number | null) =>
            val !== null ? `${val} events` : '0 events',
        },
      },
    };

    this.setInCache(cacheKey, config);
    return config;
  }

  buildEventCountByTypeChart(response: any[]): any {
    const cacheKey = this.getCacheKey('eventCountByTypeChart', response);
    const cached = this.getFromCache(cacheKey);
    if (cached) return cached;

    const filteredData = response.filter((item) => {
      const upcomingValue = (item.upcoming ?? 0) + (item.ongoing ?? 0);
      const completedValue = item.completed ?? 0;
      return upcomingValue > 0 || completedValue > 0;
    });

    if (filteredData.length === 0) {
      const emptyConfig = {
        series: [],
        chart: {
          type: 'bar',
          height: 350,
          width: '100%',
          background: '#404040',
          toolbar: { show: false },
          zoom: { enabled: false },
        },
        xaxis: { categories: [] },
        yaxis: { min: 0, max: 100, tickAmount: 5 },
        noData: {
          text: 'No event data available',
          align: 'center',
          verticalAlign: 'middle',
          style: {
            color: '#94a3b8',
            fontSize: '14px',
          },
        },
      };
      this.setInCache(cacheKey, emptyConfig);
      return emptyConfig;
    }

    const categories: string[] = [];
    const upcomingData: (number | null)[] = [];
    const completedData: (number | null)[] = [];

    filteredData.forEach((item) => {
      categories.push(item.eventTypeName);
      const upcomingValue = (item.upcoming ?? 0) + (item.ongoing ?? 0);
      const completedValue = item.completed ?? 0;
      upcomingData.push(upcomingValue);
      completedData.push(completedValue);
    });

    const maxValue = Math.max(
      ...upcomingData.filter((v) => v !== null),
      ...completedData.filter((v) => v !== null),
      0,
    );
    const chartHeight = Math.min(600, Math.max(350, categories.length * 35));
    const minWidth = Math.max(categories.length * 150, 500);

    const config = {
      series: [
        {
          name: 'Upcoming events',
          data: upcomingData,
          type: 'bar',
        },
        {
          name: 'Completed events',
          data: completedData,
          type: 'bar',
        },
      ],
      chart: {
        type: 'bar',
        height: chartHeight,
        width: '100%',
        background: '#404040',
        stacked: false,
        toolbar: { show: false },
        zoom: { enabled: false },
        scrollablePlotArea: {
          enabled: categories.length > 6,
          minWidth: minWidth,
          scrollIndicatorThumbColor: '#888',
        },
        margins: { bottom: 30 },
      },
      xaxis: {
        categories,
        labels: {
          style: { colors: '#fff', fontSize: '10px', fontWeight: 500 },
          rotate: -45,
          rotateAlways: categories.length > 4,
          trim: false,
          hideOverlappingLabels: false,
          offsetX: 0,
          offsetY: 0,
          maxHeight: 160,
        },
        axisBorder: { show: true, color: '#404040' },
        axisTicks: { show: true, color: '#404040' },
      },
      responsive: [
        {
          breakpoint: 768,
          options: {
            chart: { height: 320 },
            xaxis: {
              labels: {
                style: { fontSize: '9px' },
                rotate: -45,
                rotateAlways: true,
                offsetX: 0,
                maxHeight: 100,
              },
            },
            dataLabels: { offsetY: -12, style: { fontSize: '9px' } },
          },
        },
      ],
      yaxis: {
        min: 0,
        max: Math.ceil(maxValue / 5) * 5 || 5,
        tickAmount: 2,
        decimalsInFloat: 0,
        labels: {
          style: { colors: '#888', fontSize: '12px' },
          formatter: (value: number) => Math.round(value).toString(),
        },
        axisBorder: { show: true, color: '#404040' },
        axisTicks: { show: true, color: '#404040' },
      },
      plotOptions: {
        bar: {
          horizontal: false,
          columnWidth: '50%',
          borderRadius: 0,
          dataLabels: { position: 'top' },
          grouping: true,
          hideZeroBarsWhenGrouped: false,
        },
      },
      stroke: {
        show: true,
        width: 2,
        colors: ['transparent'],
      },
      dataLabels: {
        enabled: true,
        offsetY: -18,
        style: {
          fontSize: '10px',
          fontWeight: 'bold',
          colors: ['#ffffff', '#ffffff'],
        },
        background: {
          enabled: false,
        },
        formatter: (val: number) => (val > 0 ? val.toString() : ''),
      },
      colors: ['#2CC3E8', '#50B748'],
      grid: {
        borderColor: '#404040',
        strokeDashArray: 2,
        yaxis: { lines: { show: true } },
        xaxis: { lines: { show: false } },
      },
      tooltip: {
        enabled: true,
        theme: 'dark',
        shared: true,
        intersect: false,
        y: {
          formatter: (val: number | null) => {
            if (val === null || val === 0) return '';
            return `${val} events`;
          },
        },
      },
    };

    this.setInCache(cacheKey, config);
    return config;
  }

  private getScrollableMinWidth(
    totalBars: number,
    visibleBars: number = 10,
  ): number {
    const containerWidth =
      window.innerWidth > 768
        ? window.innerWidth * 0.6 // desktop: 60% of screen
        : window.innerWidth - 40; // mobile: full width minus padding

    const slotWidth = Math.max(
      containerWidth / visibleBars, // each bar gets equal slot
      55, // minimum 55px per bar slot
    );

    return Math.ceil(totalBars * slotWidth);
  }

  buildEventTypeChart(response: any[]): any {
    const cacheKey = this.getCacheKey('eventTypeChart', response);
    const cached = this.getFromCache(cacheKey);
    if (cached) return cached;

    let config: any;

    if (response.length > 0 && response[0].focus_area) {
      const focusAreas = Array.from(
        new Set(
          response.flatMap(
            (item) => item.focus_area?.map((fa: any) => fa.focusArea) || [],
          ),
        ),
      );

      const series = focusAreas.map((focusArea) => ({
        name: this.truncateName(focusArea as string, 20),
        data: response.map((eventItem) => {
          const focusAreaData = eventItem.focus_area?.find(
            (fa: any) => fa.focusArea === focusArea,
          );
          const focusCount = focusAreaData?.count || 0;
          const actualEventCount = eventItem.count || 0;
          const totalFocusSum =
            eventItem.focus_area?.reduce(
              (sum: number, fa: any) => sum + (fa.count || 0),
              0,
            ) || 0;

          if (totalFocusSum === 0) return 0;
          return (focusCount / totalFocusSum) * actualEventCount;
        }),
      }));

      const filteredSeries = series.filter((s) => Math.max(...s.data) > 0);
      const actualEventCounts = response.map((item) => item.count || 0);
      const { yAxisMax, tickAmount } = this.calculateAxisConfig(
        Math.max(...actualEventCounts, 0),
      );
      const categories = response.map((item) =>
        this.truncateName(item.eventType || item.label || 'Unknown', 20),
      );

      const chartHeight = Math.min(600, Math.max(350, categories.length * 35));
      const minWidth = this.getScrollableMinWidth(categories.length, 10);

      config = {
        series: filteredSeries,
        chart: {
          type: 'bar',
          height: chartHeight,
          stacked: true,
          background: '#404040',
          toolbar: { show: false },
          zoom: { enabled: false },
          animations: { enabled: true, easing: 'easeinout' },
          scrollablePlotArea: {
            enabled: true,
            minWidth: minWidth,
            scrollIndicatorThumbColor: '#888',
          },
          margins: { bottom: 60 },
        },
        xaxis: {
          categories: categories,
          tickPlacement: 'on',
          labels: {
            style: { colors: '#fff', fontSize: '11px', fontWeight: 500 },
            rotate: -45,
            rotateAlways: true,
            trim: false,
            hideOverlappingLabels: false,
            maxWidth: 300,
            offsetY: 5,
          },
          axisBorder: { show: false },
          axisTicks: { show: false },
        },
        yaxis: {
          min: 0,
          max: yAxisMax,
          tickAmount,
          decimalsInFloat: 0,
          labels: {
            style: { colors: '#888', fontSize: '11px' },
            formatter: (val: number) =>
              val > 0 ? Math.round(val).toString() : '',
          },
        },
        dataLabels: { enabled: false },
        plotOptions: {
          bar: {
            horizontal: false,
            columnWidth: '50%',
            borderRadius: 0,
            dataLabels: {
              position: 'top',
              total: {
                enabled: true,
                style: {
                  fontSize: '12px',
                  fontWeight: 'bold',
                  color: '#ffffff',
                },
                formatter: (val: number, opts: any) => {
                  const dataPointIndex = opts.dataPointIndex;
                  const actualCount = actualEventCounts[dataPointIndex];
                  return actualCount > 0
                    ? Math.round(actualCount).toString()
                    : '';
                },
                offsetY: -5,
              },
            },
          },
        },
        grid: {
          borderColor: '#555',
          strokeDashArray: 3,
          yaxis: { lines: { show: true, color: '#555' } },
        },
        legend: {
          show: true,
          position: 'bottom',
          fontSize: '10px',
          labels: { colors: '#fff' },
          markers: {
            width: 10,
            height: 10,
            radius: 50,
            shape: 'circle',
            strokeWidth: 0,
          },
        },
        tooltip: {
          custom: function ({ series, seriesIndex, dataPointIndex, w }: any) {
            const focusAreaName =
              filteredSeries[seriesIndex]?.name || 'Unknown';
            const originalFocusData = response[dataPointIndex].focus_area?.find(
              (fa: any) => fa.focusArea === focusAreaName,
            );
            const originalCount = originalFocusData?.count ?? 0;
            const eventCount = originalFocusData?.eventCount ?? 0;
            const color = w.config.colors[seriesIndex % w.config.colors.length];
            const eventTypeName =
              response[dataPointIndex]?.eventType ||
              response[dataPointIndex]?.label ||
              '';
            return (
              '<div style="padding:6px 10px;background:#1e1e1e;border-bottom:1px solid #444;color:#fff;font-size:12px;font-weight:600;">' +
              eventTypeName +
              '</div>' +
              '<div style="padding:8px 10px;background:#2a2a2a;min-width:180px;">' +
              '<div style="display:flex;align-items:center;gap:6px;margin-bottom:4px;">' +
              `<span style="width:9px;height:9px;border-radius:50%;background:${color};display:inline-block;"></span>` +
              `<span style="color:#fff;font-size:12px;">${focusAreaName}: <strong>${originalCount}</strong></span>` +
              '</div>' +
              `<div style="padding-left:15px;color:#fff;font-size:11px;">Event count: <strong style="color:#fff;">${eventCount}</strong></div>` +
              '</div>'
            );
          },
          shared: false,
          intersect: true,
        },
        colors: ['#faa633', '#2cc3e8', '#ffdd2e', '#f04e30'],
      };
    } else if (response.length > 0 && response[0].eventTypeCounts) {
      const eventTypes = Array.from(
        new Set(
          response.flatMap((item) => Object.keys(item.eventTypeCounts || {})),
        ),
      );

      const series = response.map((eventItem) => ({
        name: this.truncateName(
          eventItem.eventType || eventItem.label || 'Unknown',
          20,
        ),
        data: eventTypes.map(
          (event) => eventItem.eventTypeCounts?.[event] ?? 0,
        ),
      }));

      const stackedTotals = eventTypes.map((_, idx) =>
        series.reduce((sum, s) => sum + (s.data[idx] as number), 0),
      );
      const { yAxisMax, tickAmount } = this.calculateAxisConfig(
        Math.max(...stackedTotals, 0),
      );

      const minWidth = this.getScrollableMinWidth(eventTypes.length, 10);

      config = {
        series,
        chart: {
          type: 'bar',
          height: 400,
          stacked: true,
          background: '#404040',
          toolbar: { show: false },
          zoom: { enabled: false },
          scrollablePlotArea: {
            enabled: true,
            minWidth: minWidth,
            scrollIndicatorThumbColor: '#888',
          },
        },
        xaxis: {
          categories: eventTypes,
          tickPlacement: 'on',
          labels: {
            style: { colors: '#fff', fontSize: '11px', fontWeight: 500 },
            rotate: -45,
            rotateAlways: true,
            trim: false,
            hideOverlappingLabels: false,
          },
          axisBorder: { show: false },
          axisTicks: { show: false },
        },
        yaxis: {
          min: 0,
          max: yAxisMax,
          tickAmount,
          decimalsInFloat: 0,
          labels: {
            style: { colors: '#888', fontSize: '11px' },
            formatter: (value: number) => Math.round(value).toString(),
          },
        },
        plotOptions: {
          bar: {
            horizontal: false,
            columnWidth: '50%',
            borderRadius: 0,
            dataLabels: {
              position: 'top',
              total: {
                enabled: true,
                style: {
                  fontSize: '12px',
                  fontWeight: 'bold',
                  color: '#ffffff',
                },
                formatter: (val: number) =>
                  val > 0 ? Math.round(val).toString() : '',
                offsetY: -5,
              },
            },
          },
        },
        dataLabels: { enabled: false },
        grid: {
          borderColor: '#555',
          strokeDashArray: 3,
          yaxis: { lines: { show: true, color: '#555' } },
        },
        legend: {
          show: true,
          position: 'bottom',
          fontSize: '10px',
          labels: { colors: '#fff' },
          markers: {
            width: 10,
            height: 10,
            radius: 50,
            shape: 'circle',
            strokeWidth: 0,
          },
        },
        colors: ['#E4FFF0', '#AAFFD0', '#50B748', '#002C14', '#3C8D60'],
      };
    } else {
      const categories = response.map((item) =>
        this.truncateName(item.eventType || item.label || 'Unknown', 20),
      );
      const data = response.map((item) => item.count ?? item.value ?? 0);
      const { yAxisMax, tickAmount } = this.calculateAxisConfig(
        Math.max(...data, 0),
      );
      const chartHeight = Math.min(600, Math.max(350, categories.length * 35));
      const minWidth = this.getScrollableMinWidth(categories.length, 10);

      config = {
        series: [{ name: 'Events', data }],
        chart: {
          type: 'bar',
          height: chartHeight,
          background: '#404040',
          toolbar: { show: false },
          zoom: { enabled: false },
          scrollablePlotArea: {
            enabled: true,
            minWidth: minWidth,
            scrollIndicatorThumbColor: '#888',
          },
        },
        xaxis: {
          categories,
          tickPlacement: 'on',
          labels: {
            style: { colors: '#fff', fontSize: '11px', fontWeight: 500 },
            rotate: -45,
            rotateAlways: true,
            trim: false,
            hideOverlappingLabels: false,
          },
          axisBorder: { show: false },
          axisTicks: { show: false },
        },
        yaxis: {
          min: 0,
          max: yAxisMax,
          tickAmount,
          decimalsInFloat: 0,
          labels: {
            style: { colors: '#888', fontSize: '11px' },
            formatter: (value: number) => Math.round(value).toString(),
          },
        },
        plotOptions: {
          bar: {
            horizontal: false,
            columnWidth: '50%',
            borderRadius: 0,
            dataLabels: {
              position: 'top',
              total: {
                enabled: true,
                style: {
                  fontSize: '12px',
                  fontWeight: 'bold',
                  color: '#ffffff',
                },
                formatter: (val: number) =>
                  val > 0 ? Math.round(val).toString() : '',
                offsetY: -5,
              },
            },
          },
        },
        dataLabels: { enabled: false },
        colors: ['#E4FFF0', '#AAFFD0', '#50B748', '#002C14', '#3C8D60'],
        grid: {
          borderColor: '#555',
          strokeDashArray: 3,
          yaxis: { lines: { show: true, color: '#555' } },
        },
        legend: { show: false },
      };
    }

    this.setInCache(cacheKey, config);
    return config;
  }

  buildPartnerChart(
    data: { partnerName: string; eventsParticipated: number }[],
  ): any {
    const cacheKey = this.getCacheKey('partnerChart', data);
    const cached = this.getFromCache(cacheKey);
    if (cached) return cached;

    const categories = data.map((item) => item.partnerName);
    const values = data.map((item) => item.eventsParticipated);
    const { yAxisMax, tickAmount } = this.calculateAxisConfig(
      Math.max(...values, 0),
    );
    const chartHeight = Math.min(600, Math.max(350, categories.length * 35));

    const config = {
      series: [{ name: 'Events participated', data: values }],
      xaxis: {
        categories,
        labels: {
          style: { colors: '#fff', fontSize: '12px', fontWeight: 500 },
        },
        axisBorder: { show: false },
        axisTicks: { show: false },
      },
      yaxis: {
        min: 0,
        max: yAxisMax,
        tickAmount,
        forceNiceScale: true,
        decimalsInFloat: 0,
        labels: {
          style: { colors: '#888', fontSize: '12px' },
          formatter: (value: number) => Math.round(value).toString(),
        },
      },
      colors: ['#50B748'],
      plotOptions: {
        bar: {
          horizontal: false,
          columnWidth: '60%',
          borderRadius: 0,
          dataLabels: {
            position: 'top',
            total: {
              enabled: true,
              style: {
                fontSize: '10px',
                fontWeight: 'bold',
                color: '#ffffff',
              },
              formatter: (val: number) => (val > 0 ? val.toString() : ''),
            },
          },
        },
      },
      dataLabels: {
        enabled: true,
        offsetY: -20,
        style: { fontSize: '11px', fontWeight: 500, colors: ['#fff'] },
        formatter: (val: number) => (val > 0 ? val.toString() : ''),
      },
      chart: {
        type: 'bar',
        height: chartHeight,
        background: '#404040',
        toolbar: { show: false },
        zoom: { enabled: false },
      },
      grid: {
        borderColor: '#555',
        strokeDashArray: 4,
        yaxis: { lines: { show: true, color: '#555' } },
      },
    };

    this.setInCache(cacheKey, config);
    return config;
  }

  buildEngagedSchoolChart(apiData: any): any {
    const cacheKey = this.getCacheKey('engagedSchoolChart', apiData);
    const cached = this.getFromCache(cacheKey);
    if (cached) return cached;

    const categories = apiData.graphDataEventParticipation.map(
      (item: any) => item.label,
    );
    const eventParticipationData = apiData.graphDataEventParticipation.map(
      (item: any) => item.value,
    );

    const engagementBySchoolMap = new Map<string, any>(
      apiData.graphDataEngagementBySchool.map((item: any) => [
        item.label,
        item.value,
      ]),
    );
    const engagementBySchoolData = categories.map(
      (label: unknown) => engagementBySchoolMap.get(label as string) ?? 0,
    );

    const allValues = [...eventParticipationData, ...engagementBySchoolData];
    const maxValue = Math.max(...allValues, 0);
    const yAxisMax = Math.ceil(maxValue / 5) * 5 || 5;
    const chartHeight = Math.min(600, Math.max(350, categories.length * 35));

    const containerWidth =
      window.innerWidth > 768
        ? window.innerWidth * 0.6
        : window.innerWidth - 40;
    const slotWidth = Math.max(containerWidth / 10, 55);
    const minWidth = Math.ceil(categories.length * slotWidth);

    const config = {
      series: [
        { name: 'Upcoming events', data: eventParticipationData },
        { name: 'Completed events', data: engagementBySchoolData },
      ],
      chart: {
        type: 'bar',
        height: chartHeight,
        background: '#404040',
        stacked: true,
        toolbar: { show: false },
        zoom: { enabled: false },
        scrollablePlotArea: {
          enabled: true,
          minWidth: minWidth,
          scrollIndicatorThumbColor: '#888',
        },
      },
      xaxis: {
        categories,
        tickPlacement: 'on',
        labels: {
          formatter: (val: string) => this.formatLabel(val),
          style: { colors: '#fff', fontSize: '10px', fontWeight: 500 },
          rotate: -45,
          rotateAlways: true,
          trim: false,
          hideOverlappingLabels: false,
          maxWidth: 300,
          offsetY: 5,
        },
        axisBorder: { show: false },
        axisTicks: { show: false },
      },
      yaxis: {
        min: 0,
        max: yAxisMax,
        tickAmount: 4,
        forceNiceScale: true,
        decimalsInFloat: 0,
        labels: {
          style: { colors: '#888', fontSize: '12px' },
          formatter: (value: number) => Math.round(value).toString(),
        },
      },
      colors: ['#2CC3E8', '#50B748'],
      plotOptions: {
        bar: {
          horizontal: false,
          columnWidth: '50%',
          dataLabels: {
            position: 'top',
            total: {
              enabled: true,
              style: {
                fontSize: '10px',
                fontWeight: 'bold',
                color: '#ffffff',
              },
              formatter: (val: number) => (val > 0 ? val.toString() : ''),
            },
          },
        },
      },
      legend: { position: 'bottom', labels: { colors: '#fff' } },
    };

    this.setInCache(cacheKey, config);
    return config;
  }

  buildEventsManagedChart(response: any[]): any {
    const cacheKey = this.getCacheKey('eventsManagedChart', response);
    const cached = this.getFromCache(cacheKey);
    if (cached) return cached;

    let config: any;

    if (response.length > 0 && !response[0].eventTypeCounts) {
      config = this.buildSimpleManagerChart(response);
    } else {
      const eventTypes = Array.from(
        new Set(
          response.flatMap((item) => Object.keys(item.eventTypeCounts || {})),
        ),
      );
      const series = response.map((manager) => ({
        name: this.truncateName(manager.eventManager, 20),
        data: eventTypes.map((event) => manager.eventTypeCounts?.[event] ?? 0),
      }));

      const stackedTotals = eventTypes.map((_, idx) =>
        series.reduce((sum, s) => sum + (s.data[idx] as number), 0),
      );
      const { yAxisMax, tickAmount } = this.calculateAxisConfig(
        Math.max(...stackedTotals, 0),
      );

      const containerWidth =
        window.innerWidth > 768
          ? window.innerWidth * 0.6
          : window.innerWidth - 40;
      const slotWidth = Math.max(containerWidth / 10, 55);
      const minWidth = Math.ceil(eventTypes.length * slotWidth);

      config = {
        series,
        chart: {
          type: 'bar',
          height: 400,
          stacked: true,
          background: '#404040',
          toolbar: { show: false },
          zoom: { enabled: false },
          scrollablePlotArea: {
            enabled: true,
            minWidth: minWidth,
            scrollIndicatorThumbColor: '#888',
          },
        },
        xaxis: {
          categories: eventTypes,
          tickPlacement: 'on',
          labels: {
            style: { colors: '#fff', fontSize: '11px', fontWeight: 500 },
            rotate: -45,
            rotateAlways: true,
            trim: false,
            hideOverlappingLabels: false,
            maxWidth: 300,
            offsetY: 5,
          },
          axisBorder: { show: false },
          axisTicks: { show: false },
        },
        yaxis: {
          min: 0,
          max: yAxisMax,
          tickAmount,
          decimalsInFloat: 0,
          labels: {
            style: { colors: '#888', fontSize: '11px' },
            formatter: (value: number) => Math.round(value).toString(),
          },
        },
        plotOptions: {
          bar: {
            horizontal: false,
            columnWidth: '50%',
            borderRadius: 0,
            dataLabels: {
              position: 'top',
              total: {
                enabled: true,
                style: {
                  fontSize: '10px',
                  fontWeight: 'bold',
                  color: '#ffffff',
                },
                formatter: (val: number) => (val > 0 ? val.toString() : ''),
              },
            },
          },
        },
        grid: {
          borderColor: '#555',
          strokeDashArray: 3,
          yaxis: { lines: { show: true, color: '#555' } },
        },
        legend: {
          show: true,
          position: 'bottom',
          fontSize: '10px',
          labels: { colors: '#fff' },
          markers: {
            width: 10,
            height: 10,
            radius: 50,
            shape: 'circle',
            strokeWidth: 0,
          },
        },
        colors: ['#E4FFF0', '#AAFFD0', '#50B748', '#002C14', '#3C8D60'],
      };
    }

    this.setInCache(cacheKey, config);
    return config;
  }

  buildIndustryPartnerChart(response: any[]): any {
    const cacheKey = this.getCacheKey('eventsManagedChart', response);
    const cached = this.getFromCache(cacheKey);
    if (cached) return cached;

    let config: any;

    if (response.length === 0) {
      config = this.buildEmptyChart();
    } else {
      // X-axis: unique industry partner names
      const partnerNames = Array.from(
        new Set(
          response.flatMap(
            (eventType) =>
              eventType.industryPartners?.map((partner: any) => partner.name) ||
              [],
          ),
        ),
      );

      // Series: one per event type, data per partner
      const series = response.map((eventType) => ({
        name: eventType.eventTypeName,
        data: partnerNames.map((partnerName) => {
          const partner = eventType.industryPartners?.find(
            (p: any) => p.name === partnerName,
          );
          return partner?.count || 0;
        }),
      }));

      // yAxisMax: max total count per partner (sum of all event types)
      const maxPartnerTotal = Math.max(
        ...partnerNames.map((partnerName) =>
          response.reduce((sum, eventType) => {
            const partner = eventType.industryPartners?.find(
              (p: any) => p.name === partnerName,
            );
            return sum + (partner?.count || 0);
          }, 0),
        ),
        0,
      );
      const { yAxisMax, tickAmount } = this.calculateAxisConfig(maxPartnerTotal);

      config = {
        series: series,
        chart: {
          type: 'bar',
          height: 400,
          stacked: true,
          background: '#404040',
          toolbar: { show: false },
          zoom: { enabled: false },
        },
        xaxis: {
          categories: partnerNames,
          labels: {
            style: { colors: '#fff', fontSize: '11px', fontWeight: 500 },
            rotate: -45,
          },
          axisBorder: { show: false },
          axisTicks: { show: false },
        },
        yaxis: {
          min: 0,
          max: yAxisMax,
          tickAmount,
          decimalsInFloat: 0,
          labels: {
            style: { colors: '#888', fontSize: '11px' },
            formatter: (value: number) => Math.round(value).toString(),
          },
        },
        plotOptions: {
          bar: {
            horizontal: false,
            columnWidth: '45px',
            borderRadius: 0,
            dataLabels: {
              position: 'top',
              total: {
                enabled: true,
                style: {
                  fontSize: '10px',
                  fontWeight: 'bold',
                  color: '#ffffff',
                },
                formatter: (val: number) => (val > 0 ? val.toString() : ''),
              },
            },
          },
        },
        grid: {
          borderColor: '#555',
          strokeDashArray: 3,
          yaxis: { lines: { show: true, color: '#555' } },
        },
        legend: {
          show: true,
          position: 'bottom',
          fontSize: '10px',
          labels: { colors: '#fff' },
          markers: {
            width: 10,
            height: 10,
            radius: 50,
            shape: 'circle',
            strokeWidth: 0,
          },
        },
        colors: ['#3C8D60', '#AAFFD0', '#50B748', '#002C14', '#E4FFF0'],
      };
    }

    this.setInCache(cacheKey, config);
    return config;
  }

  private buildEmptyChart(): any {
    return {
      series: [],
      chart: {
        type: 'bar',
        height: 400,
        background: '#404040',
        toolbar: { show: false },
      },
      xaxis: {
        categories: [],
        labels: {
          style: { colors: '#fff', fontSize: '11px' },
        },
      },
      yaxis: {
        min: 0,
        max: 10,
        tickAmount: 5,
        labels: {
          style: { colors: '#888' },
        },
      },
      plotOptions: {
        bar: {
          horizontal: false,
          columnWidth: '45px',
        },
      },
      legend: {
        show: true,
        position: 'right',
        labels: { colors: '#fff' },
      },
      colors: ['#E4FFF0', '#AAFFD0', '#50B748', '#002C14', '#3C8D60'],
      noData: {
        text: 'No data available',
        align: 'center',
        verticalAlign: 'middle',
        style: {
          color: '#fff',
          fontSize: '14px',
        },
      },
    };
  }

  buildUpcomingPieChart(data: any[]): any {
    const cacheKey = this.getCacheKey('upcomingPieChart', data);
    const cached = this.getFromCache(cacheKey);
    if (cached) return cached;

    const pieData = data.map((item) => ({
      name: item.focusArea || item.label || 'Unknown',
      value: item.upcoming || item.value || 0,
    }));
    const total = pieData.reduce((sum, item) => sum + item.value, 0);

    const config = {
      series: pieData.map((item) => item.value),
      chart: {
        type: 'pie',
        height: 400,
        toolbar: { show: false },
        background: 'transparent',
      },
      labels: pieData.map((item) => item.name),
      colors: ['#50B748', '#2CC3E8'],
      legend: {
        show: true,
        position: 'bottom',
        horizontalAlign: 'center',
        fontSize: '16px',
        fontFamily: 'Arial, sans-serif',
        fontWeight: 400,
        labels: { colors: '#fff' },
        markers: { width: 12, height: 12, radius: 6 },
        itemMargin: { horizontal: 10, vertical: 5 },
        onItemClick: { toggleDataSeries: false },
        onItemHover: { highlightDataSeries: false },
      },
      dataLabels: {
        enabled: true,
        formatter: (_val: number, opts: any) => {
          const value = opts.w.config.series[opts.seriesIndex];
          return value > 0 ? `${value}` : '';
        },
        style: { fontSize: '12px', fontWeight: 'bold', colors: ['#ffffff'] },
        background: {
          enabled: false,
          foreColor: '#fff',
          padding: 4,
          borderRadius: 0,
          borderWidth: 0,
          borderColor: '#fff',
          opacity: 0.8,
        },
        dropShadow: { enabled: false },
        offsetX: 0,
        offsetY: 0,
      },
      tooltip: {
        enabled: true,
        theme: 'dark',
        y: { formatter: (value: number) => `${value} events` },
      },
      plotOptions: {
        pie: {
          expandOnClick: false,
          offsetX: 0,
          offsetY: 0,
          customScale: 1,
          dataLabels: { offset: -15 },
          donut: {
            size: '65%',
            labels: {
              show: true,
              name: { show: false },
              value: {
                show: true,
                fontSize: '24px',
                fontWeight: 'bold',
                color: '#000000',
                offsetY: -10,
                formatter: () => `${total}`,
              },
              total: {
                show: true,
                label: 'Upcoming',
                fontSize: '14px',
                fontWeight: 'normal',
                color: '#666666',
                offsetY: 10,
                formatter: () => '',
              },
            },
          },
        },
      },
      stroke: { show: false, width: 0 },
      states: {
        hover: { filter: { type: 'none' } },
        active: { filter: { type: 'none' } },
      },
      fill: { opacity: 1, gradient: { enabled: false } },
      grid: { show: false, padding: { top: 0, right: 0, bottom: 0, left: 0 } },
      responsive: [
        {
          breakpoint: 768,
          options: {
            chart: { height: 350 },
            legend: { position: 'bottom', fontSize: '10px' },
          },
        },
      ],
      noData: {
        text: 'No data available',
        align: 'center',
        verticalAlign: 'middle',
        style: {
          color: '#000000',
          fontSize: '14px',
          fontFamily: 'Arial, sans-serif',
        },
      },
    };

    this.setInCache(cacheKey, config);
    return config;
  }

  buildDiversityPieChart(data: any[]): any {
    const cacheKey = this.getCacheKey('diversityPieChart', data);
    const cached = this.getFromCache(cacheKey);
    if (cached) return cached;

    const sortedData = [...data].sort(
      (a, b) => (b.count || 0) - (a.count || 0),
    );
    const labels = sortedData.map((item) => item.ethnicityName || 'Unknown');
    const series = sortedData.map((item) => item.count || 0);
    const total = series.reduce((sum, val) => sum + val, 0);

    const colorPalette = [
      '#50B748',
      '#2CC3E8',
      '#FF6B6B',
      '#FFD93D',
      '#C084FC',
      '#FB923C',
      '#F472B6',
      '#34D399',
      '#FBBF24',
      '#6EE7B7',
      '#93C5FD',
      '#FDE68A',
      '#D8B4FE',
      '#FDA4AF',
      '#9CA3AF',
    ];

    const config = {
      labels,
      series,
      chart: {
        type: 'pie',
        height: 280,
        toolbar: { show: false },
        background: 'transparent',
      },
      colors: colorPalette.slice(0, labels.length),
      legend: {
        show: true,
        position: 'right',
        fontSize: '10px',
        fontFamily: 'Arial, sans-serif',
        labels: { colors: '#e2e8f0' },
        markers: { width: 8, height: 8, radius: 4 },
        itemMargin: { horizontal: 8, vertical: 3 },
        formatter: (seriesName: string) => seriesName,
      },
      dataLabels: { enabled: false },
      tooltip: {
        enabled: true,
        theme: 'dark',
        y: {
          formatter: (value: number, opts: any) => {
            return `No. of ethnicity ${value} (${((value / total) * 100).toFixed(1)}%)`;
          },
          title: { formatter: () => '' },
        },
      },
      plotOptions: {
        pie: {
          expandOnClick: true,
          donut: { size: '0%' },
          dataLabels: { offset: 0 },
        },
      },
      stroke: { show: true, width: 0, colors: ['#404040'] },
    };

    this.setInCache(cacheKey, config);
    return config;
  }

  buildGenderDistributionChart(data: any[]): any {
    const cacheKey = this.getCacheKey('genderDistributionChart', data);
    const cached = this.getFromCache(cacheKey);
    if (cached) return cached;

    const allGenders = [
      'female',
      'male',
      'non-identifying',
      'prefer not to say',
    ];
    const genderLabelMap: Record<string, string> = {
      female: 'Female',
      male: 'Male',
      'non-identifying': 'Non-Identifying',
      'prefer not to say': 'Prefer not to say',
    };
    const dataMap = new Map(
      data.map((item) => [
        (item.gender || item.name || '').toLowerCase(),
        item.count || item.value || 0,
      ]),
    );
    const pieData = allGenders.map((gender) => ({
      name: genderLabelMap[gender] ?? gender,
      key: gender,
      value: dataMap.get(gender) ?? 0,
    }));

    const total = pieData.reduce((sum, item) => sum + item.value, 0);
    const genderColorMap: Record<string, string> = {
      male: '#50B748',
      female: '#FAA633',
      'non-identifying': '#B45F9E',
      'prefer not to say': '#FFDD2E',
    };
    const colors = pieData.map((item) => genderColorMap[item.key] ?? '#AAFFD0');

    const config = {
      labels: pieData.map((item) => item.name),
      series: pieData.map((item) => item.value),
      chart: {
        type: 'pie',
        height: 280,
        toolbar: { show: false },
        background: 'transparent',
      },
      colors,
      legend: {
        show: true,
        position: 'right',
        fontSize: '10px',
        fontFamily: 'Arial, sans-serif',
        labels: { colors: '#e2e8f0' },
        markers: { width: 8, height: 8, radius: 4 },
        itemMargin: { horizontal: 8, vertical: 3 },
        formatter: (seriesName: string) => seriesName,
      },
      dataLabels: { enabled: false },
      tooltip: {
        enabled: true,
        theme: 'dark',
        y: {
          formatter: (value: number) =>
            `(${((value / total) * 100).toFixed(1)}%)`,
        },
      },
      plotOptions: {
        pie: {
          expandOnClick: true,
          donut: { size: '0%' },
          dataLabels: { offset: 0 },
        },
      },
      stroke: { show: true, width: 0, colors: ['#404040'] },
    };

    this.setInCache(cacheKey, config);
    return config;
  }

  buildAgeDistributionChart(data: { Age: string; count: number }[]): any {
    const cacheKey = this.getCacheKey('ageDistributionChart', data);
    const cached = this.getFromCache(cacheKey);
    if (cached) return cached;

    const sorted = [...data].sort((a, b) => Number(b.Age) - Number(a.Age));
    const n = sorted.length;

    const maxCount = Math.max(...sorted.map((d) => d.count), 0);
    const yAxisMax = Math.ceil(maxCount / 10) * 10 || 50;

    const BAR_HEIGHT = 28;
    const BAR_GAP = 16;
    const CHART_PADDING = 80;
    const MIN_HEIGHT = 300;

    const dynamicHeight = Math.max(
      MIN_HEIGHT,
      n * (BAR_HEIGHT + BAR_GAP) + CHART_PADDING,
    );

    const config = {
      series: [{ name: 'Students', data: sorted.map((d) => d.count) }],
      chart: {
        type: 'bar',
        height: `${dynamicHeight}px`,
        toolbar: { show: false },
        background: 'transparent',
        animations: { enabled: true, speed: 800 },
      },
      plotOptions: {
        bar: {
          horizontal: true,
          barHeight: `${BAR_HEIGHT}px`,
          borderRadius: 2,
          distributed: false,
          dataLabels: {
            position: 'top',
            total: {
              enabled: true,
              style: {
                fontSize: '10px',
                fontWeight: 'bold',
                color: '#ffffff',
              },
              formatter: (val: number) => (val > 0 ? val.toString() : ''),
            },
          },
        },
      },
      dataLabels: {
        enabled: false,
      },
      xaxis: {
        categories: sorted.map((d) => d.Age),
        labels: {
          style: { colors: '#aaa', fontSize: '12px' },
          offsetX: 0,
          offsetY: 0,
        },
        axisBorder: { show: false },
        axisTicks: { show: false },
        min: 0,
        max: yAxisMax,
        tickAmount: Math.min(5, Math.ceil(yAxisMax / 10)),
      },
      yaxis: {
        labels: {
          style: { colors: '#aaa', fontSize: '13px' },
          maxWidth: 100,
          offsetX: 0,
        },
      },
      grid: {
        borderColor: '#444',
        strokeDashArray: 4,
        xaxis: { lines: { show: true } },
        yaxis: { lines: { show: false } },
      },
      colors: ['#00D4FF'],
      fill: { opacity: 1, colors: ['#00D4FF'] },
      tooltip: {
        theme: 'dark',
        y: { formatter: (val: number) => `${val} students` },
      },
      theme: { mode: 'dark' },
    };

    this.setInCache(cacheKey, config);
    return config;
  }

  buildAttendanceTrendLineChart(
    data: any[],
    startDate: Date,
    endDate: Date,
  ): any {
    const cacheKey = this.getCacheKey('attendanceTrendLineChart', {
      data,
      startDate,
      endDate,
    });
    const cached = this.getFromCache(cacheKey);
    if (cached) return cached;

    let seriesData = [];

    if (data && data.length > 0) {
      if (data[0].series && Array.isArray(data[0].series)) {
        seriesData = data[0].series;
      } else if (data[0].data && Array.isArray(data[0].data)) {
        seriesData = data[0].data;
      } else if (Array.isArray(data)) {
        seriesData = data;
      }
    }

    if (!seriesData || seriesData.length === 0) {
      return this.getEmptyChartConfig();
    }

    const monthOrder = [
      'jan',
      'feb',
      'mar',
      'apr',
      'may',
      'jun',
      'jul',
      'aug',
      'sep',
      'oct',
      'nov',
      'dec',
    ];

    const sortedData = [...seriesData].sort((a, b) => {
      const yearDiff = (a.year || 0) - (b.year || 0);
      if (yearDiff !== 0) return yearDiff;
      return (
        monthOrder.indexOf(a.month?.toLowerCase()) -
        monthOrder.indexOf(b.month?.toLowerCase())
      );
    });


    const categories = sortedData.map((item: any) => {
      const month = item.month
        ? item.month.charAt(0).toUpperCase() + item.month.slice(1)
        : '';
      const year = item.year ? ` ${item.year}` : '';
      return month + year;
    });

    const attendedSeries = sortedData.map((item: any) => {
      const value = item.attended || item.value || 0;
      return typeof value === 'number' ? value : 0;
    });
    const maxValue = Math.max(...attendedSeries, 0);
    const yAxisMax =
      maxValue === 0 ? 100 : Math.ceil((maxValue * 1.1) / 100) * 100;

    const config = {
      series: [
        {
          name: 'Attended',
          data: attendedSeries,
          color: '#50B748',
        },
      ],
      chart: {
        type: 'area',
        height: 280,
        width: '100%',
        background: 'transparent',
        toolbar: { show: false },
        animations: {
          enabled: true,
          speed: 800,
          animateGradually: { enabled: true, delay: 150 },
          dynamicAnimation: { enabled: true, speed: 350 },
        },
        zoom: { enabled: false },
        fontFamily: 'Inter, system-ui, sans-serif',
      },
      colors: ['#50B748'],
      stroke: {
        curve: 'straight',
        width: 3.5,
        lineCap: 'butt',
        colors: ['#50B748'],
      },
      markers: {
        size: 4,
        colors: ['#50B748'],
        strokeColors: '#ffffff',
        strokeWidth: 2,
        hover: {
          size: 7,
          sizeOffset: 3,
        },
      },
      fill: {
        type: 'gradient',
        gradient: {
          shadeIntensity: 0.4,
          opacityFrom: 0.75,
          opacityTo: 0.1,
          stops: [0, 100],
          colorStops: [
            { offset: 0, color: '#50B748', opacity: 0.75 },
            { offset: 50, color: '#22c55e', opacity: 0.45 },
            { offset: 100, color: '#15803d', opacity: 0.1 },
          ],
        },
      },
      xaxis: {
        categories,
        labels: {
          style: {
            colors: '#aaaaaa',
            fontSize: '13px',
            fontWeight: 500,
          },
          rotate: -45,
          rotateAlways: false,
        },
        axisBorder: { show: false },
        axisTicks: { show: false },
        crosshairs: {
          show: true,
          width: 1,
          position: 'back',
          opacity: 0.9,
          stroke: { width: 1, color: '#50B748', dashArray: 3 },
        },
      },
      yaxis: {
        min: 0,
        max: yAxisMax,
        tickAmount: 5,
        labels: {
          style: { colors: '#aaaaaa', fontSize: '13px' },
          formatter: (val: number) => {
            if (val >= 1000) return (val / 1000).toFixed(1) + 'k';
            return val.toString();
          },
        },
      },
      grid: {
        borderColor: '#444444',
        strokeDashArray: 4,
        xaxis: { lines: { show: false } },
        yaxis: { lines: { show: true } },
        padding: { left: 10, right: 20, top: 20, bottom: 10 },
      },
      tooltip: {
        enabled: true,
        theme: 'dark',
        y: {
          formatter: (val: number) => val + ' attendees',
          title: { formatter: () => 'Attendance: ' },
        },
        x: {
          formatter: (val: string) => 'Month: ' + val,
        },
      },
      legend: { show: false },
      dataLabels: {
        enabled: false,
        style: { colors: ['#50B748'] },
      },
      responsive: [
        {
          breakpoint: 480,
          options: {
            chart: { height: 240 },
            markers: { size: 3 },
            yaxis: { tickAmount: 3 },
          },
        },
      ],
    };

    this.setInCache(cacheKey, config);
    return config;
  }

  buildAttendanceSuccessRateChart(
    attendedCount: number,
    absentCount: number,
  ): any {
    const cacheKey = this.getCacheKey('attendanceSuccessRateChart', {
      attendedCount,
      absentCount,
    });
    const cached = this.getFromCache(cacheKey);
    if (cached) return cached;

    const total = attendedCount + absentCount;
    const attendedPercentage =
      total > 0 ? Math.round((attendedCount / total) * 100) : 100;
    const absentPercentage = total > 0 ? 100 - attendedPercentage : 7;

    const config = {
      series: [attendedPercentage, absentPercentage],
      chart: {
        type: 'donut',
        height: 280,
        width: '100%',
        toolbar: { show: false },
        background: 'transparent',
      },
      colors: ['#50B748', '#FAA633'],
      labels: ['Attended', 'Absent'],
      dataLabels: {
        enabled: false,
        formatter: (val: number) => val + '%',
        style: { fontSize: '14px', fontWeight: 'bold', colors: ['#ffffff'] },
        dropShadow: { enabled: false },
      },
      plotOptions: {
        pie: {
          expandOnClick: false,
          donut: {
            size: '65%',
            labels: {
              show: true,
              name: {
                show: true,
                fontSize: '16px',
                fontWeight: 'normal',
                color: '#94a3b8',
                offsetY: -10,
              },
              value: {
                show: true,
                fontSize: '28px',
                fontWeight: 'bold',
                color: '#ffffff',
                offsetY: 10,
                formatter: (val: any) => val + '%',
              },
              total: {
                show: true,
                label: '',
                fontSize: '14px',
                fontWeight: 'normal',
                color: '#94a3b8',
                formatter: () => attendedPercentage + '%',
              },
            },
          },
        },
      },
      tooltip: {
        enabled: true,
        theme: 'dark',
        y: { formatter: (val: number) => val + '%' },
      },
      stroke: { show: false },
      fill: { opacity: 1 },
      responsive: [
        {
          breakpoint: 768,
          options: { chart: { height: 300 }, legend: { fontSize: '10px' } },
        },
      ],
    };

    this.setInCache(cacheKey, config);
    return config;
  }

  buildAttendanceByEventTypeChart(data: any): any {
    const cacheKey = this.getCacheKey('attendanceByEventTypeChart', data);
    const cached = this.getFromCache(cacheKey);
    if (cached) return cached;

    const chartData = data?.monthWiseAttendance ?? data;

    if (!chartData || chartData.length === 0) {
      const emptyConfig = this.getEmptyChartConfig();
      this.setInCache(cacheKey, emptyConfig);
      return emptyConfig;
    }

    const regionMap = new Map();

    chartData.forEach((item: any) => {
      const regionName = item.eventHostRegionName;
      if (!regionName) return;

      if (!regionMap.has(regionName)) {
        regionMap.set(regionName, {
          attendedCount: 0,
          notAttendedCount: 0,
        });
      }

      const regionData = regionMap.get(regionName);
      regionData.attendedCount += item.attendedCount || 0;
      regionData.notAttendedCount += item.notAttendedCount || 0;
    });

    const categories = Array.from(regionMap.keys());
    const attendedData = categories.map(
      (region) => regionMap.get(region).attendedCount,
    );
    const notAttendedData = categories.map(
      (region) => regionMap.get(region).notAttendedCount,
    );

    const nonZeroIndices = categories.map(
      (_, index) => attendedData[index] > 0 || notAttendedData[index] > 0,
    );

    const filteredCategories = categories.filter(
      (_, index) => nonZeroIndices[index],
    );
    const filteredAttendedData = attendedData.filter(
      (_, index) => nonZeroIndices[index],
    );
    const filteredNotAttendedData = notAttendedData.filter(
      (_, index) => nonZeroIndices[index],
    );

    if (filteredCategories.length === 0) {
      const emptyConfig = this.getEmptyChartConfig(
        'No attendance data available',
      );
      this.setInCache(cacheKey, emptyConfig);
      return emptyConfig;
    }

    const finalAttendedData = filteredAttendedData.map((v) =>
      v === 0 ? null : v,
    );
    const finalNotAttendedData = filteredNotAttendedData.map((v) =>
      v === 0 ? null : v,
    );

    const maxValue = Math.max(
      ...filteredAttendedData,
      ...filteredNotAttendedData,
      0,
    );

    let yAxisMax;
    if (maxValue === 0) {
      yAxisMax = 100;
    } else if (maxValue < 200) {
      yAxisMax = 200;
    } else {
      yAxisMax = Math.ceil(maxValue / 100) * 100;
    }

    const containerWidth =
      window.innerWidth > 768
        ? window.innerWidth * 0.6
        : window.innerWidth - 40;
    const slotWidth = Math.max(containerWidth / 10, 55);
    const minWidth = Math.ceil(filteredCategories.length * slotWidth);

    const config = {
      series: [
        {
          name: 'Attended',
          data: finalAttendedData,
          type: 'bar',
        },
        {
          name: 'Not Attended',
          data: finalNotAttendedData,
          type: 'bar',
        },
      ],
      chart: {
        type: 'bar',
        height: 450,
        width: '100%',
        background: '#404040',
        toolbar: { show: false },
        zoom: { enabled: false },
        stacked: false,
        scrollablePlotArea: {
          enabled: true,
          minWidth: minWidth,
          scrollIndicatorThumbColor: '#888',
        },
      },
      colors: ['#50B748', '#FAA633'],
      plotOptions: {
        bar: {
          horizontal: false,
          columnWidth: '50%',
          borderRadius: 0,
          borderRadiusApplication: 'end',
          dataLabels: {
            position: 'top',
          },
          grouping: true,
          barHeight: '100%',
          distributed: false,
        },
      },
      dataLabels: {
        enabled: true,
        offsetY: -15,
        style: { fontSize: '11px', fontWeight: 'bold', colors: ['#ffffff'] },
        formatter: (val: number) => {
          if (val === null || val <= 0) return '';
          return val.toString();
        },
      },
      xaxis: {
        categories: filteredCategories,
        tickPlacement: 'on',
        labels: {
          style: { colors: '#94a3b8', fontSize: '11px', fontWeight: 500 },
          rotate: -45,
          rotateAlways: true,
          trim: false,
          hideOverlappingLabels: false,
          maxWidth: 300,
          offsetY: 5,
        },
        axisBorder: { show: true, color: '#404040' },
        axisTicks: { show: true, color: '#404040' },
      },
      yaxis: {
        min: 0,
        max: yAxisMax,
        tickAmount: 5,
        forceNiceScale: true,
        labels: {
          style: { colors: '#94a3b8', fontSize: '11px', fontWeight: 500 },
          formatter: (value: number) => Math.round(value).toString(),
        },
      },
      grid: {
        borderColor: '#404040',
        strokeDashArray: 2,
        yaxis: { lines: { show: true } },
        xaxis: { lines: { show: false } },
      },
      stroke: {
        show: true,
        width: 2,
        colors: ['transparent'],
      },
      tooltip: {
        enabled: true,
        theme: 'dark',
        shared: true,
        intersect: false,
        y: {
          formatter: (val: number, { seriesIndex, w }: any) => {
            if (val === null || val === 0) return '0';
            const seriesName = w.config.series[seriesIndex].name;
            return `${val} ${seriesName.toLowerCase()}`;
          },
        },
      },
      legend: {
        show: true,
        position: 'bottom',
        horizontalAlign: 'center',
        fontSize: '14px',
        fontFamily: 'Arial, sans-serif',
        fontWeight: 400,
        labels: { colors: '#fff' },
        markers: {
          width: 12,
          height: 12,
          strokeWidth: 0,
          radius: 6,
          shape: 'circle',
          offsetX: 0,
          offsetY: 0,
        },
        itemMargin: { horizontal: 10, vertical: 5 },
        onItemClick: { toggleDataSeries: true },
        onItemHover: { highlightDataSeries: true },
      },
    };

    this.setInCache(cacheKey, config);
    return config;
  }

  private getEmptyChartConfig(message: string = 'No data available'): any {
    return {
      series: [],
      chart: {
        type: 'bar',
        height: 450,
        width: '100%',
        background: '#404040',
        toolbar: { show: false },
        zoom: { enabled: false },
        stacked: false,
      },
      xaxis: { categories: [] },
      yaxis: { min: 0, max: 100, tickAmount: 5 },
      noData: {
        text: message,
        align: 'center',
        verticalAlign: 'middle',
        style: {
          color: '#94a3b8',
          fontSize: '14px',
          fontFamily: 'Arial, sans-serif',
        },
      },
      tooltip: {
        enabled: false,
      },
    };
  }

  // ================================================================
  // PRIVATE HELPERS
  // ================================================================

  private buildSimpleManagerChart(response: any[]): any {
    const categories = response.map((item) =>
      this.truncateName(item.eventManager, 20),
    );
    const values = response.map((item) => item.totalEvents || 0);
    const { yAxisMax, tickAmount } = this.calculateAxisConfig(
      Math.max(...values, 0),
    );
    const chartHeight = Math.min(600, Math.max(350, categories.length * 35));

    return {
      series: [{ name: 'Total Events', data: values }],
      chart: {
        type: 'bar',
        height: chartHeight,
        background: '#404040',
        toolbar: { show: false },
        zoom: { enabled: false },
      },
      xaxis: {
        categories,
        labels: { style: { colors: '#fff', fontSize: '9px', fontWeight: 500 } },
        axisBorder: { show: false },
        axisTicks: { show: false },
      },
      yaxis: {
        min: 0,
        max: yAxisMax,
        tickAmount,
        decimalsInFloat: 0,
        labels: {
          style: { colors: '#888', fontSize: '11px' },
          formatter: (value: number) => Math.round(value).toString(),
        },
      },
      plotOptions: {
        bar: {
          horizontal: false,
          columnWidth: '60px',
          borderRadius: 0,
          dataLabels: {
            enabled: true,
            position: 'top',
            offsetY: -5,
            style: { fontSize: '9px', fontWeight: 500, colors: ['#fff'] },
            formatter: (val: number) => (val > 0 ? val.toString() : ''),
          },
        },
      },
      colors: ['#50B748'],
      grid: {
        borderColor: '#555',
        strokeDashArray: 3,
        yaxis: { lines: { show: true, color: '#555' } },
      },
      legend: { show: false },
    };
  }

  private getMonthsInRange(
    startDate: Date,
    endDate: Date,
    apiData: any[],
  ): any[] {
    if (!startDate || !endDate) return apiData || [];

    const MONTHS = [
      '',
      'January',
      'February',
      'March',
      'April',
      'May',
      'June',
      'July',
      'August',
      'September',
      'October',
      'November',
      'December',
    ];

    const dataMap = new Map<string, any>();
    (apiData || []).forEach((item) => {
      const monthKey = new Date(
        `${MONTHS[item.month]} 1, ${item.year}`,
      ).toLocaleString('default', { month: 'short' });
      dataMap.set(`${monthKey}-${item.year}`, item);
    });

    const result: any[] = [];
    const current = new Date(startDate.getFullYear(), startDate.getMonth(), 1);
    const end = new Date(endDate.getFullYear(), endDate.getMonth(), 1);

    while (current <= end) {
      const monthName = current.toLocaleString('default', { month: 'short' });
      const year = current.getFullYear();
      const key = `${monthName}-${year}`;
      const existing = dataMap.get(key);

      result.push(
        existing
          ? {
              ...existing,
              month: monthName,
              monthYear: `${monthName} ${year}`,
              year,
            }
          : {
              month: monthName,
              monthYear: `${monthName} ${year}`,
              year,
              upcoming: 0,
              completed: 0,
            },
      );
      current.setMonth(current.getMonth() + 1);
    }
    return result;
  }

  private calculateYAxisMax(maxValue: number): number {
    if (maxValue <= 5) return 5;
    if (maxValue <= 10) return 10;
    if (maxValue <= 20) return 20;
    if (maxValue <= 50) return 50;
    return Math.ceil(maxValue / 10) * 10;
  }

  private calculateAxisConfig(maxValue: number): {
    yAxisMax: number;
    tickAmount: number;
  } {
    let yAxisMax: number;
    let tickAmount: number;

    if (maxValue <= 5) {
      yAxisMax = 5;
      tickAmount = 5;
    } else if (maxValue <= 10) {
      yAxisMax = Math.ceil(maxValue / 2) * 2;
      tickAmount = yAxisMax / 2;
    } else if (maxValue <= 20) {
      yAxisMax = Math.ceil(maxValue / 5) * 5;
      tickAmount = yAxisMax / 5;
    } else if (maxValue <= 50) {
      yAxisMax = Math.ceil(maxValue / 10) * 10;
      tickAmount = yAxisMax / 10;
    } else {
      yAxisMax = Math.ceil(maxValue / 10) * 10;
      tickAmount = yAxisMax / 10;
    }

    return { yAxisMax, tickAmount: Math.max(2, Math.round(tickAmount)) };
  }

  private getTickInterval(yAxisMax: number): number {
    if (yAxisMax <= 10) return 2;
    if (yAxisMax <= 20) return 4;
    if (yAxisMax <= 50) return 10;
    return Math.ceil(yAxisMax / 5);
  }

  private formatLabel(val: string): string | string[] {
    const maxCharsPerLine = 12;
    const words = val?.replace(/-/g, ' - ').split(' ') ?? [];
    const lines: string[] = [];
    let currentLine = '';

    words.forEach((word) => {
      if (!word) return;
      const testLine = currentLine ? `${currentLine} ${word}` : word;
      if (testLine.length <= maxCharsPerLine) {
        currentLine = testLine;
      } else {
        if (currentLine) lines.push(currentLine);
        currentLine = word;
      }
    });
    if (currentLine) lines.push(currentLine);
    return lines.length > 1 ? lines : lines[0] || '';
  }

  private truncateName(name: string, maxLength: number): string {
    if (!name || name.length <= maxLength) return name || '';
    const parts = name.split(' ');
    if (parts.length > 1) {
      const first = parts[0];
      const last = parts[parts.length - 1];
      if (first.length + last.length + 3 <= maxLength) {
        return `${first} ${last.charAt(0)}.`;
      }
    }
    return name.substring(0, maxLength - 3) + '...';
  }
}
